import { Membre, Relation } from '../types';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

function gedcomDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function exportGEDCOM(membres: Membre[], relations: Relation[]): void {
  const lines: string[] = [];

  lines.push('0 HEAD');
  lines.push('1 SOUR ArbreFamilial');
  lines.push('2 VERS 1.0');
  lines.push('1 GEDC');
  lines.push('2 VERS 5.5.1');
  lines.push('1 CHAR UTF-8');
  lines.push('1 LANG French');

  // Familles : une FAM par relation conjugale
  const conjugal = relations.filter(r => ['conjoint', 'ex_conjoint'].includes(r.type_relation));
  // Déduplique les paires (A-B = B-A)
  const famMap = new Map<string, { id: number; p1: number; p2: number; type: string }>();
  conjugal.forEach(r => {
    const key = [Math.min(r.membre_source, r.membre_cible), Math.max(r.membre_source, r.membre_cible)].join('-');
    if (!famMap.has(key)) famMap.set(key, { id: famMap.size + 1, p1: r.membre_source, p2: r.membre_cible, type: r.type_relation });
  });

  // Pour chaque membre, trouver ses FAMs (en tant que conjoint) et ses FAMC (enfant dans)
  const memberFams = new Map<number, number[]>();  // membre → liste de fam IDs (FAMS)
  const memberFamc = new Map<number, number[]>();  // membre → liste de fam IDs (FAMC)

  famMap.forEach(fam => {
    [fam.p1, fam.p2].forEach(pid => {
      if (!memberFams.has(pid)) memberFams.set(pid, []);
      memberFams.get(pid)!.push(fam.id);
    });
  });

  // Enfants : trouver la FAM d'appartenance pour chaque enfant
  const parentRels = relations.filter(r => r.type_relation === 'parent');
  parentRels.forEach(r => {
    const childId = r.membre_cible;
    const parentId = r.membre_source;
    // Chercher une FAM où ce parent est conjoint
    let famId: number | null = null;
    famMap.forEach(fam => {
      if (fam.p1 === parentId || fam.p2 === parentId) famId = fam.id;
    });
    if (famId !== null) {
      if (!memberFamc.has(childId)) memberFamc.set(childId, []);
      const existing = memberFamc.get(childId)!;
      if (!existing.includes(famId)) existing.push(famId);
    }
  });

  // Enregistrements INDI
  membres.forEach(m => {
    lines.push(`0 @I${m.id}@ INDI`);
    const nom = m.nom_jeune_fille ? `${m.prenom} /${m.nom_jeune_fille}/` : `${m.prenom} /${m.nom}/`;
    lines.push(`1 NAME ${nom}`);
    if (m.sexe === 'Homme') lines.push('1 SEX M');
    else if (m.sexe === 'Femme') lines.push('1 SEX F');
    else lines.push('1 SEX U');

    if (m.date_naissance) {
      lines.push('1 BIRT');
      lines.push(`2 DATE ${gedcomDate(m.date_naissance)}`);
      if (m.lieu_naissance) lines.push(`2 PLAC ${m.lieu_naissance}`);
    }
    if (m.date_deces) {
      lines.push('1 DEAT Y');
      lines.push(`2 DATE ${gedcomDate(m.date_deces)}`);
      if (m.lieu_deces) lines.push(`2 PLAC ${m.lieu_deces}`);
    }
    if (m.notes) lines.push(`1 NOTE ${m.notes.replace(/\n/g, ' ')}`);

    (memberFams.get(m.id) || []).forEach(fid => lines.push(`1 FAMS @F${fid}@`));
    (memberFamc.get(m.id) || []).forEach(fid => lines.push(`1 FAMC @F${fid}@`));
  });

  // Enregistrements FAM
  famMap.forEach(fam => {
    lines.push(`0 @F${fam.id}@ FAM`);
    const m1 = membres.find(m => m.id === fam.p1);
    const m2 = membres.find(m => m.id === fam.p2);
    // Mari = Homme, Femme = épouse, sinon ordre original
    const husb = m1?.sexe === 'Homme' ? fam.p1 : m2?.sexe === 'Homme' ? fam.p2 : fam.p1;
    const wife = husb === fam.p1 ? fam.p2 : fam.p1;
    lines.push(`1 HUSB @I${husb}@`);
    lines.push(`1 WIFE @I${wife}@`);
    if (fam.type === 'conjoint') lines.push('1 MARR');

    // Enfants de cette famille (dédupliqués)
    const famChildren = new Set<number>();
    parentRels.forEach(r => {
      if (r.membre_source === fam.p1 || r.membre_source === fam.p2) {
        famChildren.add(r.membre_cible);
      }
    });
    famChildren.forEach(cid => lines.push(`1 CHIL @I${cid}@`));
  });

  lines.push('0 TRLR');

  const content = lines.join('\r\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'arbre-familial.ged';
  a.click();
  URL.revokeObjectURL(url);
}
